/**
 * AgentsDashboardApiClient -- Estado live de los Argys del ecosistema.
 *
 * V2 (T4 fix bitacora #638): usa endpoint dedicado GET /api/v1/agents/dashboard
 * que JOIN con nexo_private.nexo_agents (solo agentes registrados, filtra
 * service-events nx-panel/nexo-api/sync-upload). Fallback a bitacora-agrupada
 * si el endpoint no responde (compat con ambientes pre-T3).
 *
 * Reglas de clasificacion canonicas (T4 fix):
 *   - WIP      -> verde
 *   - DONE     -> azul
 *   - BLOCKED  -> AMARILLO (Argy-d #631 lo necesitaba visible)
 *   - REDIRECT -> morado (distinto de WIP, cambio de scope)
 *   - IDLE     -> gris (sin actividad <30min)
 *
 * @module infrastructure/api/AgentsDashboardApiClient
 */

import { ApiBase } from './ApiBase.js';
import { BitacoraApiClient } from './BitacoraApiClient.js';
import { AGENT_CODES, normalizeAgentCode, displayNameFor, esOrigenDeProceso } from './AgentRegistry.js';

const DASHBOARD_ENDPOINT = '/api/v1/agents/dashboard';

// Codigos canonicos UPPERCASE de los Argys del ecosistema. DERIVADO del registro
// unico (PANEL-AGENTS-DASHBOARD-V1-001 B: drift kill). Usado para fallback
// restrictive filter cuando el endpoint dedicado no esta disponible -
// excluye service-events nx-panel/nexo-api/sync-upload que NO son agentes.
export const KNOWN_AGENTS = AGENT_CODES;

const STATUS_REGEX = {
  wip:        /^\[WIP\]/i,
  done:       /^\[DONE\]/i,
  blocked:    /^\[BLOCKED\]/i,
  redirect:   /^\[REDIRECT\]/i,
  unblock:    /^\[UNBLOCK\]/i,
  heartbeat:  /^\[HEARTBEAT\]/i,
  approve:    /^\[APPROVE\]/i,
  assign:     /^\[ASSIGN\]/i,
  review_ok:  /^\[REVIEW-OK\]/i,
  review_fail:/^\[REVIEW-FAIL\]/i,
  // La API la trata como apagado (AgentStatusPolicy de AGENTS-V11). Aqui faltaba.
  session_close:/^\[SESSION-CLOSE\]/i
};

const IDLE_THRESHOLD_MS = 60 * 60 * 1000;       // 1h sin actividad => idle
const OFF_THRESHOLD_MS  = 24 * 60 * 60 * 1000;  // 24h sin actividad => off

/**
 * Normaliza el origen de bitacora -> codigo canonico UPPERCASE.
 * A (alineacion identidad): 'ares-001' y 'ARES-001' resuelven al MISMO codigo.
 * Delega en el registro unico para no duplicar la convencion.
 */
function originToAgentCode(origen) {
  return normalizeAgentCode(origen);
}

function classifyEvent(titulo) {
  if (!titulo) return 'unknown';
  for (const [status, regex] of Object.entries(STATUS_REGEX)) {
    if (regex.test(titulo)) return status;
  }
  return 'other';
}

/**
 * Dado los eventos ordenados desc del agente, deriva su estado live actual.
 * Si el ultimo es HEARTBEAT, usa el penultimo evento "de trabajo" (WIP/DONE/etc).
 */
function deriveAgentStatus(events, now = Date.now()) {
  if (!events || events.length === 0) {
    return { status: 'off', lastEvent: null, lastTs: null, missionCode: null };
  }

  const last = events[0];
  const lastTs = new Date(last.tsUtc || last.ts_utc || last.created_at).getTime();
  const ageMs = now - lastTs;

  if (ageMs > OFF_THRESHOLD_MS) {
    return { status: 'off', lastEvent: last, lastTs, missionCode: extractMission(last) };
  }

  let workEvent = last;
  let workType = classifyEvent(last.titulo);

  // Si el ultimo es heartbeat, ignorarlo y buscar evento "real" anterior
  if (workType === 'heartbeat') {
    workEvent = events.find(e => classifyEvent(e.titulo) !== 'heartbeat') || last;
    workType = classifyEvent(workEvent.titulo);
  }

  // ESPEJO DE LA POLITICA QUE YA VIVE EN LA API (AGENTS-V11, Argy-A).
  //
  // Este camino solo corre cuando el endpoint dedicado falla, y hasta ahora
  // tenia una regla DISTINTA a la de la API: conocia cuatro marcadores y
  // mandaba todo lo demas a 'idle'. O sea que el dia que la API se cayera, el
  // tablero no se rompia: se ponia a mentir con la misma cara de siempre. Me
  // lo paso Argy-A en #10558 y lo medi antes de tocarlo.
  //
  // LO QUE MEDI, ventana de 7 dias sobre la bitacora real (944 entradas, del
  // 28-ago al 4-sep): el panel mapeaba 326 y las otras 618 caian en 'idle'.
  // Son DOS TERCIOS. Y el marcador mas usado de todos es [COORD], con 206
  // entradas -- mas que [DONE] --, o sea que lo que mas hace la flota era
  // justo lo que se veia como que no pasaba nada.
  //
  // La regla de la API, que aqui se copia en vez de reinventarse: un marcador
  // DESCONOCIDO significa que el agente esta activo. Solo dicen "no trabaja"
  // los que lo declaran. [STOP] queda fuera de esa lista a proposito -- es el
  // borde que encontro Argy-Q: lo postea un orquestador SOBRE OTRO, y apagaba
  // al que da la orden.
  let status;
  if (workType === 'wip')                                         status = 'wip';
  else if (workType === 'blocked')                                status = 'blocked';
  else if (workType === 'redirect')                               status = 'redirect';
  else if (workType === 'assign')                                 status = 'assigned';
  else if (workType === 'session_close')                          status = 'off';
  else if (workType === 'heartbeat')                              status = 'idle';
  else if (workType === 'done')                                   status = ageMs < IDLE_THRESHOLD_MS ? 'done' : 'idle';
  else                                                            status = 'active';

  return {
    status,
    lastEvent: last,
    lastTs,
    workEvent,
    workType,
    ageMs,
    missionCode: extractMission(workEvent)
  };
}

function extractMission(event) {
  if (!event?.notas) return null;
  // notas puede venir como string JSON o objeto ya parseado
  let notas = event.notas;
  if (typeof notas === 'string') {
    try { notas = JSON.parse(notas); } catch { return null; }
  }
  return notas?.mission_code || notas?.missionCode || null;
}

// #2454(c): target_agent de un [ASSIGN] (lo postea el orquestador, no el agente).
function extractTargetAgent(event) {
  if (!event?.notas) return null;
  let notas = event.notas;
  if (typeof notas === 'string') { try { notas = JSON.parse(notas); } catch { return null; } }
  const t = notas?.target_agent || notas?.targetAgent;
  return t ? String(t).toUpperCase() : null;
}
function eventTs(ev) {
  return new Date(ev?.tsUtc || ev?.ts_utc || ev?.created_at || 0).getTime();
}

const RECENT_ACTIVE_MS = 30 * 60 * 1000;   // <30min => activo (no idle)

/**
 * #2454(c) — overlay VERAZ de estado sobre los agentes ya derivados (endpoint o fallback).
 * Corrige 3 casos que la card no veia (solo leia eventos por origen del agente):
 *   (1) Ultimo [ASSIGN] con target_agent=agente SIN WIP/DONE posterior -> ASSIGNED.
 *   (2) Agente con post <30min NO debe salir IDLE/OFF (recencia).
 *   (3) DONE >12h con un [ASSIGN] posterior pendiente -> ASSIGNED (trabajo nuevo en cola).
 *
 * @param {Array} agents  - agentes con { agentCode, status, lastEvent{titulo,tsUtc}, lastTs, missionCode }
 * @param {Array} events  - eventos recientes de bitacora (para extraer los [ASSIGN])
 */
function applyVerazOverlay(agents, events, now = Date.now()) {
  // Ultimo [ASSIGN] por target_agent.
  const lastAssign = new Map();
  for (const ev of (events || [])) {
    if (classifyEvent(ev.titulo) !== 'assign') continue;
    const target = extractTargetAgent(ev);
    if (!target) continue;
    const ts = eventTs(ev);
    const cur = lastAssign.get(target);
    if (!cur || ts > cur.ts) lastAssign.set(target, { ev, ts });
  }

  for (const a of agents) {
    const lastTs = a.lastTs || (a.lastEvent?.tsUtc ? new Date(a.lastEvent.tsUtc).getTime() : 0);
    const ageMs = lastTs ? (now - lastTs) : Infinity;
    const lastType = classifyEvent(a.lastEvent?.titulo);

    // (2) recencia: actividad <30min no puede leerse IDLE/OFF.
    if ((a.status === 'idle' || a.status === 'off') && ageMs < RECENT_ACTIVE_MS) {
      a.status = (lastType === 'done') ? 'done'
        : (['wip', 'blocked', 'redirect'].includes(lastType) ? lastType : 'active');
    }

    // (1)+(3): si el ultimo [ASSIGN] es mas nuevo que el ultimo TRABAJO del agente
    // (WIP/DONE/BLOCKED/REDIRECT), tiene trabajo en cola sin empezar -> ASSIGNED.
    // Cubre (1) assign sin WIP/DONE posterior y (3) DONE viejo con assign posterior.
    const assign = lastAssign.get(a.agentCode);
    if (assign) {
      const workedAfterAssign = lastTs > assign.ts &&
        ['wip', 'done', 'blocked', 'redirect'].includes(lastType);
      if (!workedAfterAssign) {
        a.status = 'assigned';
        a.missionCode = extractMission(assign.ev) || a.missionCode;
        a.assignedAt = assign.ts;
        a.assignTitulo = assign.ev.titulo;
      }
    }
  }
  return agents;
}

const ASSIGNED_COLD_MS = 24 * 60 * 60 * 1000; // ASSIGN sin WIP en 24h => frio

/**
 * PANEL-AGENTS-DASHBOARD-V1-001 (C: estado AGREGADO, no ultimo-evento).
 * Pedido por Josse: el card debe reflejar el estado REAL agregado, no solo el
 * ultimo evento. Corrige 2 lecturas falsas:
 *   (1) BLOCKED abierto: un [BLOCKED] de la mision M SIN [UNBLOCK]/[DONE]/[REVIEW-OK]
 *       posterior de ESA mision debe pesar MAS que un [DONE] posterior de OTRA mision.
 *   (2) ASSIGNED-frio: si el agente esta 'assigned' y el [ASSIGN] tiene mas de 24h
 *       sin un [WIP] posterior, se marca frio (assignedCold) — trabajo en cola sin arrancar.
 *
 * @param {Array} agents - agentes ya pasados por applyVerazOverlay
 * @param {Array} events - eventos recientes de bitacora (desc o asc, se ordena por ts)
 */
function applyAggregateStatus(agents, events, now = Date.now()) {
  const evs = Array.isArray(events) ? events : [];
  // Indice de eventos por agente (codigo canonico).
  const byAgent = new Map();
  for (const ev of evs) {
    const code = normalizeAgentCode(ev.origen);
    if (!code) continue;
    if (!byAgent.has(code)) byAgent.set(code, []);
    byAgent.get(code).push(ev);
  }

  // Resolucion de una mision: existe [UNBLOCK]/[DONE]/[REVIEW-OK] de ESA mision
  // con ts posterior al [BLOCKED] (por cualquiera: el agente se auto-desbloquea o Ares).
  const resolvesBlock = (mission, afterTs) => {
    if (!mission) return false;
    for (const ev of evs) {
      const t = classifyEvent(ev.titulo);
      if (t !== 'unblock' && t !== 'done' && t !== 'review_ok') continue;
      if (extractMission(ev) !== mission) continue;
      if (eventTs(ev) > afterTs) return true;
    }
    return false;
  };

  for (const a of agents) {
    const own = (byAgent.get(a.agentCode) || []).slice().sort((x, y) => eventTs(y) - eventTs(x));

    // (1) BLOCKED abierto: el [BLOCKED] mas reciente del agente con mission_code,
    // sin resolucion posterior de esa mision.
    const lastBlocked = own.find(e => classifyEvent(e.titulo) === 'blocked' && extractMission(e));
    if (lastBlocked) {
      const mission = extractMission(lastBlocked);
      const bTs = eventTs(lastBlocked);
      if (!resolvesBlock(mission, bTs)) {
        a.status = 'blocked';
        a.blockedOpen = true;
        a.missionCode = mission;
        a.lastEvent = a.lastEvent || { titulo: lastBlocked.titulo, tsUtc: lastBlocked.tsUtc };
      }
    }

    // (2) ASSIGNED-frio: assigned con assign >24h y sin WIP posterior.
    if (a.status === 'assigned' && a.assignedAt) {
      const wipAfter = own.some(e => classifyEvent(e.titulo) === 'wip' && eventTs(e) > a.assignedAt);
      if (!wipAfter && (now - a.assignedAt) > ASSIGNED_COLD_MS) a.assignedCold = true;
    }
  }
  return agents;
}

/**
 * Mapea status string del backend (UPPERCASE) a code interno lowercase usado
 * por la View para indexar STATUS_META. T4 fix: agrega 'redirect' como estado
 * canonico (antes solo wip/done/blocked/idle).
 */
function normalizeStatus(s) {
  if (!s) return 'idle';
  const lower = String(s).toLowerCase();
  if (['wip', 'done', 'blocked', 'redirect', 'idle', 'off', 'assigned', 'active'].includes(lower)) return lower;
  return 'idle';
}

export const AgentsDashboardApiClient = {
  /**
   * Estado live de los Argys del ecosistema.
   *
   * Estrategia T4 (post bitacora #638):
   *   1. Intenta GET /api/v1/agents/dashboard (endpoint dedicado, JOIN nexo_agents).
   *   2. Si falla (legacy/404/error red), fallback restrictive a BitacoraApiClient
   *      filtrando eventos cuyo origen NO esta en KNOWN_AGENTS canonical.
   *
   * @param {number} limit - eventos a pedir en fallback (default 100)
   * @returns {Promise<{ ok:boolean, agents?:array, error?, source? }>}
   *          source: 'endpoint' | 'bitacora-fallback'
   */
  async getAgentsState(limit = 100) {
    // Bitacora reciente: necesaria para el overlay VERAZ (#2454 c) de los [ASSIGN]
    // (que la postea el orquestador, no el agente) en AMBAS rutas (endpoint y fallback).
    const bit = await BitacoraApiClient.list(limit);
    const bitPayload = bit.ok ? (bit.data?.data ?? bit.data ?? []) : [];
    const bitEvents = Array.isArray(bitPayload) ? bitPayload : (bitPayload?.items || []);
    const now = Date.now();

    // === Intento 1: endpoint dedicado ===
    const direct = await ApiBase.get(DASHBOARD_ENDPOINT);
    if (direct.ok) {
      const payload = direct.data?.data ?? direct.data ?? {};
      const apiAgents = Array.isArray(payload.agents) ? payload.agents : [];
      const agents = apiAgents.map(a => ({
        agentCode: a.code,
        displayName: a.displayName,
        status: normalizeStatus(a.status),
        lastEvent: a.lastEvent
          ? { titulo: a.lastEvent.titulo, tsUtc: a.lastEvent.tsUtc }
          : null,
        lastTs: a.lastEvent?.tsUtc ? new Date(a.lastEvent.tsUtc).getTime() : null,
        missionCode: a.currentMissionCode || null,
        heartbeatMinAgo: a.heartbeatMinAgo,
        nxsLastMonth: a.nxsLastMonth,
        recentEvents: a.recentEvents || [],
        eventCount: a.eventCount ?? (a.lastEvent ? 1 : 0)
      }));
      // A: displayName canonico del registro si el backend no lo trae.
      for (const a of agents) if (!a.displayName) a.displayName = displayNameFor(a.agentCode);
      const overlaid = applyAggregateStatus(applyVerazOverlay(agents, bitEvents, now), bitEvents, now);
      return { ok: true, agents: overlaid, source: 'endpoint' };
    }

    // === Intento 2: fallback restrictive a bitacora ===
    if (!bit.ok) return { ok: false, error: bit.error };
    const events = bitEvents;

    // LA PERTENENCIA NO LA DECIDE ESTA LISTA (Argy-Q #10940, aplicado tambien aqui).
    //
    // Antes este camino descartaba todo origen que no estuviera en KNOWN_AGENTS,
    // que sale del registro del panel: un agente nuevo escribia y aqui no
    // existia. Es exactamente el fallo que Q encontro en El Gremio, y este lado
    // lo repetia por su cuenta -- y encima en el modo DEGRADADO, que es cuando
    // uno mas necesita creerle a la pantalla.
    //
    // Ahora salen los PROCESOS por nombre (ORIGENES_NO_AGENTE, la misma lista
    // que ya usaba la Sala de Maquinas para el chip "trabajando", ahora en el
    // registro y no copiada) y entra cualquier otro. Se siembra con
    // KNOWN_AGENTS para que un agente registrado que no escribio siga
    // apareciendo quieto en vez de desaparecer.
    //
    // POR QUE ESTE LADO DEL ERROR Y NO EL OTRO: si un origen basura se cuela,
    // sale un muneco con su codigo crudo -- feo, evidente, y alguien lo arregla
    // en el dia. Con la lista blanca, un agente de VERDAD no salia y nadie lo
    // notaba nunca. Entre el fallo que se ve y el que se esconde, este equipo
    // elige el que se ve.
    // MEDIDO el 4-sep contra la BD: los unicos origenes de los ultimos 7 dias
    // que no estan en nexo_agents son SYNC-UPLOAD (863) y NEXO-API (26), y los
    // dos estan en ORIGENES_NO_AGENTE. Hoy no se cuela nadie.
    const byAgent = new Map();
    for (const code of KNOWN_AGENTS) byAgent.set(code, []);

    for (const ev of events) {
      const code = originToAgentCode(ev.origen);
      if (!code || esOrigenDeProceso(code)) continue; // fuera los procesos
      if (!byAgent.has(code)) byAgent.set(code, []);
      byAgent.get(code).push(ev);
    }

    const agents = [];
    for (const [code, agentEvents] of byAgent.entries()) {
      const state = deriveAgentStatus(agentEvents, now);
      agents.push({
        agentCode: code,
        displayName: displayNameFor(code), // A: displayName canonico
        status: state.status,
        lastEvent: state.lastEvent,
        lastTs: state.lastTs,
        missionCode: state.missionCode,
        recentEvents: agentEvents.slice(0, 5),
        eventCount: agentEvents.length
      });
    }

    const overlaid = applyAggregateStatus(applyVerazOverlay(agents, events, now), events, now);
    return { ok: true, agents: overlaid, source: 'bitacora-fallback' };
  },

  /**
   * Util para tests: clasificar 1 titulo a su tipo.
   */
  classifyEvent,

  /** Util para tests: normalizar status string. */
  normalizeStatus,

  /** Util para tests: overlay veraz de estado (#2454 c). */
  applyVerazOverlay,

  /** Util para tests: overlay AGREGADO (C: block abierto + assigned-frio). */
  applyAggregateStatus
};
